import { createSelector } from 'reselect'
import { getLocaleDateStringFromSeconds } from 'lib/date'
import { getOwnerId } from './url'
import {
  getCollectionOwnerName,
  getCollectionTitle,
  getCollectionTypes,
  getCreatedAt,
  getIsCollectionPrivate,
} from './collection'
import { getFirstResultsArtwork, getHitsAvailableForSaleCount, getHitsTotal } from './algolia'

export const getCreatedAtDate = createSelector(
  getCreatedAt,
  (createdAt): string => getLocaleDateStringFromSeconds(createdAt) || ''
)
export const getBreadcrumbSchema = createSelector(
  [getCollectionOwnerName, getOwnerId],
  (collectionOwnerName, ownerId) => {
    return {
      '@context': 'http://schema.org',
      type: 'BreadcrumbList',
      itemListElement: [
        {
          type: 'ListItem',
          position: 1,
          item: {
            '@id': 'https://www.saatchiart.com/all',
            name: 'All Artworks',
          },
        },
        {
          type: 'ListItem',
          position: 2,
          item: {
            '@id': 'https://www.saatchiart.com/collections',
            name: 'Collections',
          },
        },
        {
          type: 'ListItem',
          position: 3,
          item: {
            '@id': `https://www.saatchiart.com//account/collection/${ownerId}`,
            name: `${collectionOwnerName} Collections`,
          },
        },
      ],
    }
  }
)
export const getMetaData = createSelector(
  [
    getBreadcrumbSchema,
    getCollectionOwnerName,
    getCollectionTitle,
    getCollectionTypes,
    getCreatedAtDate,
    getFirstResultsArtwork,
    getHitsAvailableForSaleCount,
    getHitsTotal,
    getIsCollectionPrivate,
  ],
  (
    breadcrumb,
    name,
    collectionTitle,
    collectionTypes,
    createdAt,
    firstArtworkImage,
    availableForSaleCount,
    totalHits,
    isCollectionPrivate
  ): Record<string, any> => {
    const title = `${collectionTitle} | Saatchi Art`
    const description = `${totalHits} Original Artworks curated by ${name}, ${collectionTitle}. Original Art Collection created on ${createdAt}.`

    /**
     * We only allow indexing only the following collections:
     *  - Non-private collections
     *  - Collections with at least 1 artwork for sale
     *  - Collections that have 10 artworks or more
     *  - Collections that are not one of type: `aa selection`|`community`
     */
    const hasRestrictedTypes = collectionTypes
      .map((collectionType) => collectionType?.toLowerCase())
      .some((collectionType) => ['aa selection', 'community'].includes(collectionType))
    const allowRobots =
      !isCollectionPrivate && availableForSaleCount > 0 && totalHits >= 10 && !hasRestrictedTypes

    return {
      description,
      title,
      og: {
        title,
        type: 'article',
        ttl: '2419200',
        image: firstArtworkImage,
      },
      preloadImages: [],
      robots: allowRobots,
      schema: {
        breadcrumb,
        webpage: {
          headline: title,
          description,
        },
      },
    }
  }
)
